import { CalendarDay, DateRange } from './types';

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const getDaysBetween = (startDate: Date | null, endDate: Date | null): number | null => {
  if (!startDate || !endDate) {
    return null;
  }

  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // Include both start and end days
};

export const generateCalendarDays = (month: Date, range: DateRange): CalendarDay[] => {
  const days: CalendarDay[] = [];
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);

  // Add days from previous month
  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, monthIndex, -i);
    days.push(createCalendarDay(date, range, false));
  }

  // Add days of current month
  for (let date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
    days.push(createCalendarDay(new Date(date), range, true));
  }

  // Add days from next month
  const remainingDays = 42 - days.length; // 6 rows * 7 days = 42
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, monthIndex + 1, i);
    days.push(createCalendarDay(date, range, false));
  }

  return days;
};

const createCalendarDay = (date: Date, range: DateRange, isCurrentMonth: boolean): CalendarDay => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
  
  const isToday = date.toDateString() === today.toDateString();
  const isDisabled = date > today; // Disable future dates
  const isSelected = isDateSelected(date, range);
  const isInRange = isDateInRange(date, range);
  const isRangeStart = isDateEqual(date, range.startDate);
  const isRangeEnd = isDateEqual(date, range.endDate);

  return {
    date,
    isCurrentMonth,
    isToday,
    isDisabled,
    isSelected,
    isInRange,
    isRangeStart,
    isRangeEnd,
  };
};

const isDateEqual = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return date1.toDateString() === date2.toDateString();
};

const isDateSelected = (date: Date, range: DateRange): boolean => {
  return isDateEqual(date, range.startDate) || isDateEqual(date, range.endDate);
};

const isDateInRange = (date: Date, range: DateRange): boolean => {
  if (!range.startDate || !range.endDate) return false;
  return date >= range.startDate && date <= range.endDate;
};
